// CampaignLinkDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Link } from 'lucide-react';

/**
 * Dialog for linking a deliverable to a marketing campaign
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Array} props.campaigns - List of available campaigns
 * @param {string} props.selectedCampaignId - Currently selected campaign ID
 * @param {function} props.onCampaignChange - Callback when campaign selection changes
 * @param {function} props.onLink - Callback when link is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const CampaignLinkDialog = ({
  open,
  onOpenChange,
  campaigns = [],
  selectedCampaignId,
  onCampaignChange,
  onLink,
  onCancel
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5 text-blue-500" />
            Link to Campaign
          </DialogTitle>
          <DialogDescription>
            Link this deliverable to a marketing campaign as an asset
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label>Select Campaign</Label>
            <Select value={selectedCampaignId} onValueChange={onCampaignChange}>
              <SelectTrigger data-testid="campaign-select">
                <SelectValue placeholder="Choose a campaign..." />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map(campaign => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({campaign.status})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedCampaignId && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                This deliverable will be added as an asset to the selected campaign.
                You can view all linked AI work from the campaign&apos;s detail page.
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="campaign-link-cancel">
            Cancel
          </Button>
          <Button
            disabled={!selectedCampaignId}
            onClick={onLink}
            data-testid="campaign-link-submit"
          >
            <Link className="h-4 w-4 mr-2" />
            Link to Campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignLinkDialog;
